import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useI18n } from "@/locales/i18n-provider";
import type { AppLocale } from "@/locales/types";
import { applyEmbeddedAppearance, useEmbeddedTheme } from "./embedded-appearance";
import { embeddedConfig, isAllowedParentOrigin } from "./embedded-config";
import { embeddedAccessTokenProvider } from "./embedded-token-provider";
import type {
  EmbeddedBusinessContext,
  EmbeddedNotification,
  EmbeddedTheme,
  ForbiddenReason,
  SessionEndReason,
  TickbirdToParentMessage,
  TokenRequiredReason,
  UnsavedWorkReason,
} from "./protocol";
import { FRAME_MESSAGE, PARENT_MESSAGE, parseParentMessage } from "./protocol";

/**
 * iframe 协议的唯一封装（设计 §8.3）。
 *
 * 业务页面只消费这里归一化后的上下文，**不允许**任何 Feature 组件直接访问
 * `window.parent` 或依赖父子协议（§5.1）。
 */

/** 建链失败的确定性原因，各自对应一张错误页。 */
export type EmbeddedConnectionState =
  | { status: "connecting" }
  | { status: "connected" }
  | { status: "not-embedded" }
  | { status: "invalid-config" }
  | { status: "handshake-timeout" }
  | { status: "session-ended"; reason: SessionEndReason | "unauthorized" };

export type EmbeddedNavigationRequest = { seq: number; path: string };

export type EmbeddedHostContextValue = {
  connection: EmbeddedConnectionState;
  connected: boolean;
  /** 宿主推送的外观，页面据此排版；两者都不落任何浏览器存储（§5.5.3）。 */
  locale: AppLocale;
  theme: EmbeddedTheme;
  businessContext?: EmbeddedBusinessContext;
  /** 建链时由宿主固定指定，离开业务对象不会清除。 */
  projectCode?: string;
  /** 宿主通过已认证 MessagePort 发来的站内路由切换请求。 */
  navigationRequest?: EmbeddedNavigationRequest;
  /** 宿主在 connect 时给出的深链，已校验过 `/embedded` 白名单。 */
  initialPath?: string;
  hostSessionId?: string;
  openStandalone: (path: string) => void;
  notifyReady: () => void;
  notifyForbidden: (reason: ForbiddenReason) => void;
  reportNavigation: (path: string, title: string) => void;
  reportResize: (contentHeight: number, minHeight: number) => void;
  reportNotification: (notification: EmbeddedNotification) => void;
  reportUnreadCount: (unreadCount: number) => void;
  /** 由编辑器类组件登记未保存状态，Provider 汇总后发 `navigation:block`（§5.5.4）。 */
  setUnsavedWork: (key: string, reason?: UnsavedWorkReason) => void;
  clearUnsavedWork: (key: string) => void;
  sendTelemetry: (name: string, correlationId?: string) => void;
};

const EmbeddedHostContext = createContext<EmbeddedHostContextValue | null>(null);

const HANDSHAKE_TIMEOUT_MS = 10_000;

/** 至少 128 bit 随机，握手只使用一次（§8.1）。 */
function createFrameNonce(): string {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

function readReferrerOrigin(): string | null {
  if (!document.referrer) {
    return null;
  }
  try {
    const origin = new URL(document.referrer).origin;
    return origin === "null" ? null : origin;
  } catch {
    return null;
  }
}

export function useEmbeddedHost(): EmbeddedHostContextValue {
  const value = useContext(EmbeddedHostContext);
  if (!value) {
    throw new Error("useEmbeddedHost 必须在 EmbeddedHostProvider 之内使用。");
  }
  return value;
}

export function EmbeddedHostProvider({ children }: { children: ReactNode }) {
  const { locale } = useI18n();
  const theme = useEmbeddedTheme();
  const [connection, setConnection] = useState<EmbeddedConnectionState>({ status: "connecting" });
  const [businessContext, setBusinessContext] = useState<EmbeddedBusinessContext | undefined>(undefined);
  const [projectCode, setProjectCode] = useState<string | undefined>(undefined);
  const [navigationRequest, setNavigationRequest] = useState<EmbeddedNavigationRequest | undefined>(undefined);
  const [initialPath, setInitialPath] = useState<string | undefined>(undefined);
  const [hostSessionId, setHostSessionId] = useState<string | undefined>(undefined);

  const portRef = useRef<MessagePort | null>(null);
  const nonceRef = useRef<string | null>(null);
  const contextSeqRef = useRef(0);
  const navigationSeqRef = useRef(0);
  const unsavedWorkRef = useRef(new Map<string, UnsavedWorkReason>());
  const blockedRef = useRef(false);

  const post = useCallback((message: TickbirdToParentMessage) => {
    // 建链后所有通信只走专属 MessagePort，不再回到全局 window message 总线（§8.1）。
    portRef.current?.postMessage(message);
  }, []);

  useEffect(() => {
    if (!embeddedConfig.allowAnyParentOrigin && embeddedConfig.parentOrigins.length === 0) {
      // 配了宿主列表却没有有效 Origin 时，不把错误配置当成留空放行。
      setConnection({ status: "invalid-config" });
      return;
    }

    if (window.parent === window) {
      setConnection({ status: "not-embedded" });
      return;
    }

    const nonce = createFrameNonce();
    nonceRef.current = nonce;

    const handleWindowMessage = (event: MessageEvent) => {
      if (!isAllowedParentOrigin(event.origin) || event.source !== window.parent) {
        return;
      }

      const message = parseParentMessage(event.data);
      if (message?.type !== PARENT_MESSAGE.connect) {
        return;
      }
      if (message.protocolVersion !== embeddedConfig.protocolVersion) {
        console.warn("拒绝建链：父子协议版本不匹配。");
        return;
      }
      // nonce 一次性：本次加载已建链后不再接受任何 connect（AR-18）。
      if (message.frameNonce !== nonceRef.current || portRef.current) {
        return;
      }

      const port = event.ports[0];
      if (!port) {
        return;
      }

      portRef.current = port;
      port.onmessage = handlePortMessage;
      port.start();
      setHostSessionId(message.hostSessionId);
      setProjectCode(message.projectCode);
      setInitialPath(message.initialPath);
      setConnection({ status: "connected" });
      port.postMessage({ type: FRAME_MESSAGE.connected, protocolVersion: embeddedConfig.protocolVersion } satisfies TickbirdToParentMessage);
    };

    const handlePortMessage = (event: MessageEvent) => {
      const message = parseParentMessage(event.data);
      if (!message) {
        return;
      }

      switch (message.type) {
        case PARENT_MESSAGE.tokenUpdate:
          embeddedAccessTokenProvider.applyToken(message.seq, message.accessToken, message.expiresAt);
          break;
        case PARENT_MESSAGE.contextUpdate:
          // 快速切换上下文时只接受最新序号，旧的直接丢（§13）。
          if (message.seq > contextSeqRef.current) {
            contextSeqRef.current = message.seq;
            setBusinessContext(message.context ?? undefined);
          }
          break;
        case PARENT_MESSAGE.navigationUpdate:
          if (message.seq > navigationSeqRef.current) {
            navigationSeqRef.current = message.seq;
            setNavigationRequest({ seq: message.seq, path: message.path });
          }
          break;
        case PARENT_MESSAGE.appearanceUpdate:
          applyEmbeddedAppearance(message.seq, message.locale, message.theme);
          break;
        case PARENT_MESSAGE.sessionEnd:
          // 安全事件优先于草稿：不受 navigation:block 约束，内存连同草稿一起丢（§5.5.4）。
          embeddedAccessTokenProvider.reset();
          setConnection({ status: "session-ended", reason: message.reason });
          portRef.current?.close();
          portRef.current = null;
          break;
        default:
          break;
      }
    };

    window.addEventListener("message", handleWindowMessage);

    // 能从 referrer 解析出允许的 Origin 就定向发；限制宿主时逐一发送。
    // 留空放行且 referrer 不可用时，用 `*` 发送不含凭证的 hello。
    const referrerOrigin = readReferrerOrigin();
    const targets =
      referrerOrigin && isAllowedParentOrigin(referrerOrigin) ? [referrerOrigin] : embeddedConfig.allowAnyParentOrigin ? ["*"] : embeddedConfig.parentOrigins;
    for (const target of targets) {
      window.parent.postMessage({ type: FRAME_MESSAGE.hello, protocolVersion: embeddedConfig.protocolVersion, frameNonce: nonce }, target);
    }

    const timeoutTimer = window.setTimeout(() => {
      setConnection((current) => (current.status === "connecting" ? { status: "handshake-timeout" } : current));
    }, HANDSHAKE_TIMEOUT_MS);

    // BFCache / 页面隐藏时丢弃内存 Token，恢复后重新握手（AR-23）。
    const handlePageHide = () => {
      embeddedAccessTokenProvider.reset();
    };
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      window.clearTimeout(timeoutTimer);
      window.removeEventListener("message", handleWindowMessage);
      window.removeEventListener("pagehide", handlePageHide);
      portRef.current?.close();
      portRef.current = null;
      nonceRef.current = null;
      embeddedAccessTokenProvider.reset();
    };
  }, []);

  // Provider 在 React 之前就被装配（main.tsx），这里补上它通往父子协议的出口。
  useEffect(() => {
    return embeddedAccessTokenProvider.attachBridge({
      requestToken: (reason: TokenRequiredReason) => {
        post({ type: FRAME_MESSAGE.tokenRequired, reason });
      },
      endSession: () => {
        // 换票后仍然 401 是终止态：明确告知宿主，绝不能再发 token:required
        // 诱导它继续签发。消息先发，随后立即丢 Token 和关闭专属通道。
        post({ type: FRAME_MESSAGE.sessionEnded, reason: "token-failure" });
        embeddedAccessTokenProvider.reset();
        setConnection({ status: "session-ended", reason: "unauthorized" });
        portRef.current?.close();
        portRef.current = null;
      },
    });
  }, [post]);

  const syncNavigationBlock = useCallback(() => {
    const entries = Array.from(unsavedWorkRef.current.values());
    const blocked = entries.length > 0;
    if (blocked === blockedRef.current) {
      return;
    }
    blockedRef.current = blocked;
    post({ type: FRAME_MESSAGE.navigationBlock, blocked, ...(blocked ? { reason: entries[0] } : {}) });
  }, [post]);

  const value = useMemo<EmbeddedHostContextValue>(
    () => ({
      connection,
      connected: connection.status === "connected",
      locale,
      theme,
      businessContext,
      projectCode,
      navigationRequest,
      initialPath,
      hostSessionId,
      openStandalone: (path: string) => {
        // 顶层导航一律交给宿主决定，iframe 自己不跳（§12.2）。
        post({ type: FRAME_MESSAGE.openStandalone, path });
      },
      notifyReady: () => {
        post({ type: FRAME_MESSAGE.ready });
      },
      notifyForbidden: (reason: ForbiddenReason) => {
        post({ type: FRAME_MESSAGE.forbidden, reason });
      },
      reportNavigation: (path: string, title: string) => {
        post({ type: FRAME_MESSAGE.navigationChanged, path, title });
      },
      reportResize: (contentHeight: number, minHeight: number) => {
        post({ type: FRAME_MESSAGE.resize, contentHeight, minHeight });
      },
      reportNotification: (notification: EmbeddedNotification) => {
        post({ type: FRAME_MESSAGE.notificationReceived, notification });
      },
      reportUnreadCount: (unreadCount: number) => {
        post({ type: FRAME_MESSAGE.notificationUnreadCountChanged, unreadCount });
      },
      setUnsavedWork: (key: string, reason: UnsavedWorkReason = "draft") => {
        unsavedWorkRef.current.set(key, reason);
        syncNavigationBlock();
      },
      clearUnsavedWork: (key: string) => {
        unsavedWorkRef.current.delete(key);
        syncNavigationBlock();
      },
      sendTelemetry: (name: string, correlationId?: string) => {
        post({ type: FRAME_MESSAGE.telemetry, name, ...(correlationId ? { correlationId } : {}) });
      },
    }),
    [businessContext, connection, hostSessionId, initialPath, locale, navigationRequest, post, projectCode, syncNavigationBlock, theme],
  );

  return <EmbeddedHostContext.Provider value={value}>{children}</EmbeddedHostContext.Provider>;
}
